import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormGroup,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  ConfigurationResponseDto,
  RecommendationEngineCacheRefreshRequestDto,
  UpdateConfigurationDto,
} from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "../../../../utils/api";
import { useAuthHeaders } from "../../../../hooks/useAuthHeaders";
import {
  RECOMMENDATION_ENGINE_CONFIG_KEYS,
  RECOMMENDATION_ENGINE_ENDPOINT_LABELS,
} from "../../../../utils/recommendationEngine/constants";
import { RecommendationEngineEndpoint } from "../../../../types/recommendationEngine";
import { toastError, toastSuccess } from "../../../../utils/pageUtil/toast";
import InfoTooltip from "./InfoTooltip";
import { GLOBAL_SETTINGS_TOOLTIPS } from "../copy";
import { engineTokens } from "../theme";

const SECTION_ENDPOINTS: RecommendationEngineEndpoint[] = [
  "nearby_picks",
  "trending_now",
  "browse_by_interest",
  "smart_picks",
];

const toEndpointEnum = (
  endpoint: RecommendationEngineEndpoint,
): RecommendationEngineCacheRefreshRequestDto.EndpointsEnum => {
  switch (endpoint) {
    case "similar":
      return RecommendationEngineCacheRefreshRequestDto.EndpointsEnum.Similar;
    case "recommended":
      return RecommendationEngineCacheRefreshRequestDto.EndpointsEnum
        .Recommended;
    case "nearby_picks":
      return RecommendationEngineCacheRefreshRequestDto.EndpointsEnum
        .NearbyPicks;
    case "trending_now":
      return RecommendationEngineCacheRefreshRequestDto.EndpointsEnum
        .TrendingNow;
    case "browse_by_interest":
      return RecommendationEngineCacheRefreshRequestDto.EndpointsEnum
        .BrowseByInterest;
    case "smart_picks":
    default:
      return RecommendationEngineCacheRefreshRequestDto.EndpointsEnum
        .SmartPicks;
  }
};

const parseIdList = (value: string): string[] =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const RecommendationEngineGlobalSettings = () => {
  const authHeaders = useAuthHeaders();
  const [isLoading, setIsLoading] = useState(true);
  const [literalMax, setLiteralMax] = useState(1000000);
  const [isSaving, setIsSaving] = useState(false);

  const [cityIdsInput, setCityIdsInput] = useState("");
  const [customerIdsInput, setCustomerIdsInput] = useState("");
  const [selectedEndpoints, setSelectedEndpoints] = useState<
    RecommendationEngineEndpoint[]
  >([]);
  const [isQueuingCity, setIsQueuingCity] = useState(false);
  const [isQueuingWarm, setIsQueuingWarm] = useState(false);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const maxRes =
        await adminApiService.gfMiscellaneousAdminControllerGetConfigurationByKey(
          RECOMMENDATION_ENGINE_CONFIG_KEYS.literalAbsMax,
          authHeaders,
        );
      const maxVal = (maxRes as ConfigurationResponseDto).value;
      setLiteralMax(typeof maxVal === "number" ? maxVal : Number(maxVal) || 1000000);
    } catch (error) {
      console.error(error);
      toastError("Failed to load recommendation engine settings.");
    } finally {
      setIsLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveConfig = async (key: string, value: boolean | number) => {
    setIsSaving(true);
    try {
      const dto: UpdateConfigurationDto = { value: value as unknown as { [key: string]: unknown } };
      await adminApiService.gfMiscellaneousAdminControllerUpdateConfiguration(
        dto,
        key,
        authHeaders,
      );
      toastSuccess("Settings updated.");
      await loadSettings();
    } catch (error) {
      console.error(error);
      toastError("Failed to update setting.");
    } finally {
      setIsSaving(false);
    }
  };

  const refreshBody = useMemo(
    (): RecommendationEngineCacheRefreshRequestDto => ({
      cityIds: parseIdList(cityIdsInput),
      customerIds: parseIdList(customerIdsInput),
      endpoints: selectedEndpoints.map(toEndpointEnum),
    }),
    [cityIdsInput, customerIdsInput, selectedEndpoints],
  );

  const queueCityRefresh = async () => {
    setIsQueuingCity(true);
    try {
      const response =
        await adminApiService.recommendationEngineAdminControllerQueueCityCacheRefresh(
          refreshBody,
          authHeaders,
        );
      toastSuccess(
        `Queued ${response?.queued ?? 0} city cache refresh job${
          response?.queued === 1 ? "" : "s"
        }.`,
      );
    } catch (error) {
      console.error(error);
      toastError("Failed to queue city cache refresh.");
    } finally {
      setIsQueuingCity(false);
    }
  };

  const queueWarmSetRefresh = async () => {
    setIsQueuingWarm(true);
    try {
      const response =
        await adminApiService.recommendationEngineAdminControllerQueueWarmSetRefresh(
          refreshBody,
          authHeaders,
        );
      toastSuccess(
        `Queued ${response?.queued ?? 0} warm-set refresh job${
          response?.queued === 1 ? "" : "s"
        }.`,
      );
    } catch (error) {
      console.error(error);
      toastError("Failed to queue warm-set cache refresh.");
    } finally {
      setIsQueuingWarm(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Saved recommendation engine configurations are live immediately. Use this
        tab for shared validation settings.
      </Alert>

      <Box sx={{ mt: 3, maxWidth: 360 }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle2">
            Max absolute literal value
          </Typography>
          <InfoTooltip title={GLOBAL_SETTINGS_TOOLTIPS.literalAbsMax} />
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          Any literal in a saved formula whose absolute value exceeds this
          number is rejected by the server. Lower it to harden against typos;
          raise it only when an experiment genuinely needs very large weights.
        </Typography>
        <TextField
          fullWidth
          size="small"
          type="number"
          value={literalMax}
          onChange={(e) => setLiteralMax(Number(e.target.value))}
        />
        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 1 }}
          disabled={isSaving}
          onClick={() =>
            saveConfig(RECOMMENDATION_ENGINE_CONFIG_KEYS.literalAbsMax, literalMax)
          }
        >
          {isSaving ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Save max literal value"
          )}
        </Button>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box sx={{ maxWidth: 720 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Cold-start cache maintenance
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 2 }}
        >
          Section endpoints (Nearby Picks, Trending Now, Browse by Interest,
          Smart Picks) serve from a city fallback cache and a personalized
          warm-set cache. Queue rebuilds after a configuration change so
          shoppers receive the new ranking on their next request.
        </Typography>

        <FormGroup sx={{ mb: 2 }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Section endpoints to refresh
            </Typography>
            <InfoTooltip
              title={GLOBAL_SETTINGS_TOOLTIPS.cacheRefreshEndpoints}
            />
          </Box>
          <FormControl size="small" fullWidth sx={{ maxWidth: 480 }}>
            <InputLabel>Endpoints (leave empty for all)</InputLabel>
            <Select
              multiple
              value={selectedEndpoints}
              input={<OutlinedInput label="Endpoints (leave empty for all)" />}
              onChange={(e) =>
                setSelectedEndpoints(
                  e.target.value as RecommendationEngineEndpoint[],
                )
              }
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as RecommendationEngineEndpoint[]).map(
                    (endpoint) => (
                      <Chip
                        key={endpoint}
                        size="small"
                        label={RECOMMENDATION_ENGINE_ENDPOINT_LABELS[endpoint]}
                        sx={{
                          backgroundColor: engineTokens.brandSofter,
                          color: engineTokens.brand,
                        }}
                      />
                    ),
                  )}
                </Box>
              )}
            >
              {SECTION_ENDPOINTS.map((endpoint) => (
                <MenuItem key={endpoint} value={endpoint}>
                  <Checkbox
                    checked={selectedEndpoints.includes(endpoint)}
                  />
                  <ListItemText
                    primary={RECOMMENDATION_ENGINE_ENDPOINT_LABELS[endpoint]}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormGroup>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            mb: 2,
          }}
        >
          <Box>
            <Box sx={{ display: "inline-flex", alignItems: "center", mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                City IDs (city refresh)
              </Typography>
              <InfoTooltip
                title={GLOBAL_SETTINGS_TOOLTIPS.cacheRefreshCityIds}
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Comma-separated city UUIDs"
              value={cityIdsInput}
              onChange={(e) => setCityIdsInput(e.target.value)}
            />
          </Box>
          <Box>
            <Box sx={{ display: "inline-flex", alignItems: "center", mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Customer IDs (warm-set refresh)
              </Typography>
              <InfoTooltip
                title={GLOBAL_SETTINGS_TOOLTIPS.cacheRefreshCustomerIds}
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Comma-separated customer UUIDs"
              value={customerIdsInput}
              onChange={(e) => setCustomerIdsInput(e.target.value)}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Box sx={{ display: "inline-flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              color="primary"
              disabled={isQueuingCity}
              onClick={queueCityRefresh}
            >
              {isQueuingCity ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Queue city cache refresh"
              )}
            </Button>
            <InfoTooltip title={GLOBAL_SETTINGS_TOOLTIPS.refreshCityCache} />
          </Box>
          <Box sx={{ display: "inline-flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              color="primary"
              disabled={isQueuingWarm}
              onClick={queueWarmSetRefresh}
            >
              {isQueuingWarm ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Queue warm-set refresh"
              )}
            </Button>
            <InfoTooltip title={GLOBAL_SETTINGS_TOOLTIPS.refreshWarmSetCache} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RecommendationEngineGlobalSettings;
